import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

// API/Components
import axiosInstance from '../components/axios';
import PasswordResetDialog from '../components/password_reset';
import { formatDateTime } from '../components/enums';
import CreateUserDialog from '../components/user_create';
import Toast from '../components/toast';
import TempPassDialog from '../components/temp_password';
import NoItemsOverlay from '../components/no_items_overlay';

// Material UI
import {
	DataGrid,
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton,
	GridToolbarDensitySelector,
} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CreateIcon from '@mui/icons-material/Create';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import PasswordIcon from '@mui/icons-material/Password';
import LockResetIcon from '@mui/icons-material/LockReset';

const renderYesNo = (checked) => (
	<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
		<p><strong>{ checked ? 'Yes' : 'No' }</strong></p>
	</div>
);

export default function UserManagement() {

	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
	});
	const [tempPassOpen, setTempPassOpen] = useState({ open: false, user: '', password: '' });

	const [user, setUser] = useState({});
	const [userList, setUserList] = useState([]);
	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [density, setDensity] = useState('compact');

	// "Clone User" functionality
	const [cloneUser, setCloneUser] = useState(null);

	const navigate = useNavigate();

	const [userColumns, setUserColumns] = useState([
		{
			field: 'first_name',
			headerName: 'First Name',
			headerAlign: "left",
			description: 'User\'s First Name',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'last_name',
			headerName: 'Last Name',
			headerAlign: "left",
			description: 'User\'s Last Name',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'email',
			headerName: 'Email',
			headerAlign: "left",
			description: 'User\'s Email Address',
			minWidth: 250,
			flex: 1,
		},
		{
			field: 'job_title',
			headerName: 'Job Title',
			headerAlign: "left",
			description: 'User\'s Job Title',
			minWidth: 200,
			flex: 1,
		},
		{
			field: 'company',
			headerName: 'Company',
			headerAlign: "left",
			description: 'What company the User works for',
			minWidth: 200,
			flex: 1,
		},
		{
			field: 'time_zone',
			headerName: 'Time Zone',
			headerAlign: "left",
			description: 'The Time Zone the User is in.',
			minWidth: 200,
			flex: 1,
			// renderCell: (params) => {
				// Display the Time Zone in a more readable format
				// return "This is the timezone in a more readbale format"
			// },
		},
		{
			field: 'metric',
			headerName: 'Meas. Units',
			headerAlign: "left",
			description: 'Whether to show measurement units in Metric or Imperial',
			minWidth: 110,
			flex: 1,
			renderCell: (params) => params.value ? 'Metric (°C)' : 'Imperial (°F)',
		},
		{
			field: 'is_staff',
			headerName: 'Is Admin',
			headerAlign: "center",
			description: 'Whether or not the user can manage other users\' access',
			minWidth: 100,
			flex: 1,
			renderCell: (params) => renderYesNo(params.value),
		},
		{
			field: 'is_active',
			headerName: 'Enabled',
			headerAlign: "center",
			description: 'Whether or not the user\'s account is enabled',
			minWidth: 100,
			flex: 1,
			renderCell: (params) => renderYesNo(params.value),
		},
		{
			field: 'date_joined',
			headerName: 'Account Created',
			headerAlign: "left",
			description: 'The date the User\'s account was created',
			minWidth: 185,
			flex: 1,
			renderCell: (params) => formatDateTime(params.value),
		},
		{
			field: 'last_login',
			headerName: 'Last Login',
			headerAlign: "left",
			description: 'The date the User last logged in',
			minWidth: 185,
			flex: 1,
			renderCell: (params) => formatDateTime(params.value),
		},
		{
			field: 'pass_last_reset',
			headerName: 'Pass Last Reset',
			headerAlign: "left",
			description: 'The date the User last reset their password',
			minWidth: 185,
			flex: 1,
			renderCell: (params) => formatDateTime(params.value),
		},
		{
			field: 'custom',
			headerName: 'Edit',
			headerAlign: "center",
			description: 'Edit the selected User',
			width: 65,
			disableColumnSorting: true,
			sortable: false,
			filterable: false,
			hideable: false,
			renderCell: (params)=> {
				return (
					<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<IconButtonWithMenu rowData={params.row} setCloneUser={setCloneUser} setCreateDialogOpen={setCreateDialogOpen} />
					</div>
				);
			}
		},
	]);

	// Refresh the users list
	function refreshUserList() {
		axiosInstance.get(`/users/list`).then(response => {		
			setUserList(response.data);
		});
	}

	// Get data about the user on the page
	useEffect(() => {
		axiosInstance.get(`/users/`).then(response => {		
			const userInfo = response.data;

			// Check if the user is a superuser - no editing of superusers access
			if (!userInfo || userInfo.length === 0 || (!userInfo.is_staff && !userInfo.is_superuser)) {
				navigate('/403/', { replace: true });
			}

			setUser(response.data);
			
			// Enable an additional column if the user is a superuser
			if (response.data.is_superuser && userColumns.find(column => column.field === 'is_superuser') == undefined) {
				let newUserColumns = [...userColumns];
				
				newUserColumns.splice(7, 0, {
					field: 'is_superuser',
					headerName: 'Is Superuser',
					headerAlign: "center",
					description: 'Whether or not the user is a superuser',
					minWidth: 100,
					flex: 1,
					renderCell: (params) => renderYesNo(params.value),
				});
				setUserColumns(newUserColumns);
			}
		});
	}, []);

	// Get the list of users
	useEffect(() => {
		refreshUserList();
	}, []);

	const handleNewUserClick = (event) => {
		setCreateDialogOpen(true);
	};

	const handleNewUserDialogOpen = (isOpen) => {
		if (!isOpen) {
			setCreateDialogOpen(false);
			refreshUserList();
		}
	}

	// Build the user edit menu
	function IconButtonWithMenu({ rowData, setCloneUser, setCreateDialogOpen }) {

		const [anchorEl, setAnchorEl] = useState(null);
		const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
		const [openPasswordResetDialog, setOpenPasswordResetDialog] = useState(false);

		// Enable or disable the selected user account
		function changeAccountIsActive(enabled = true) {
			axiosInstance.put(`/users/change/${rowData.id}/`, { data: { is_active: enabled } })
				.then(response => {
					let action = enabled ? 'enabled' : 'disabled';
					setToast({ severity: 'success', message: 'The user account has been ' + action, open: true });
					refreshUserList();
				})
				.catch(error => {
					setToast({ severity: 'error', message: `Error disabling user account ${rowData.email}: ${error}`, open: true });
					console.log(`Error disabling user account ${rowData.email}: ${error}`);
				});
		}

		// Force the selected user account to reset their password
		function changeForcedPassReset() {
			axiosInstance.put(`/users/change/${rowData.id}/`, { data: { password_reset: true } })
				.then(response => {
					setToast({ severity: 'success', message: 'The user will be forced to reset their password on next login.', open: true });
					refreshUserList();
				})
				.catch(error => {
					setToast({ severity: 'error', message: `Error setting forced password reset for user account ${rowData.email}: ${error}`, open: true });
					console.log(`Error setting forced password reset for user account ${rowData.email}: ${error}`);
				});
		}

		// Delete user account
		function deleteUserAccount() {
			axiosInstance.delete(`/users/delete/${rowData.id}/`)
				.then(response => {
					setToast({ severity: 'success', message: 'The user account has been deleted', open: true });
					refreshUserList();
				})
				.catch(error => {
					setToast({ severity: 'error', message: `Error deleting user account ${rowData.email}: ${error}`, open: true });
					console.log(`Error deleting user account ${rowData.email}: ${error}`);
				});
		}

		// Reset user password with a password reset email. If that fails, fall back to resetting the password to a temporary password
		function resetUserPassword() {
			axiosInstance.post(`password_reset/`, {
					email: rowData.email,
				})
				.then((res) => {
					setToast({ severity: 'success', message: 'A password reset email has been sent to: ' + rowData.email, open: true });
				})
				.catch((error) => {
					setToast({ severity: 'error', message: 'Error sending password reset email: ' + error, open: true });
					console.log(`Error sending password reset email to ${rowData.email}: ${error}`);

					// If the email sending fails, reset the password to a temporary password and display to the admin
					resetUserPasswordTemp();
				});
		}

		// Reset user password to temp password
		function resetUserPasswordTemp() {
			axiosInstance.post(`/users/passreset/`, { id: rowData.id })
				.then(response => {
					let userName = rowData.first_name + ' ' + rowData.last_name;
					setTempPassOpen({ open: true, user: userName, password: response.data.new_password });
				})
				.catch(error => {
					setToast({ severity: 'error', message: `Error resetting user account ${rowData.email}'s password: ${error}`, open: true });
					console.log(`Error resetting user account ${rowData.email}'s password: ${error}`);
				});
		}

		// Menu click
		const handleMenuClick = (event) => {
			setAnchorEl(event.currentTarget);
		};

		const handleMenuClose = () => {
			setAnchorEl(null);
		}

		// Menu Items clicks
		const handleEditClick = () => {
			handleMenuClose();
			navigate(`/admin/users/${rowData.id}`);
		};

		const handlePasswordResetClick = () => {
			handleMenuClose();
			if (rowData.is_self) {
				setOpenPasswordResetDialog(true);
			} else {
				if (window.confirm(`Are you sure you want to reset the password for this user?`)) {
					resetUserPassword();
				}
			}
		};
		
		const handleForcedPassResetClick = () => {
			handleMenuClose();
			if (!rowData.password_reset) {
				changeForcedPassReset();
			} else {
				setToast({ severity: 'info', message: `User account ${rowData.id} is already set to reset their password on next login.`, open: true });
			}
		};

		const handleCloneUserClick = () => {
			handleMenuClose();
			setCloneUser(rowData);
			setCreateDialogOpen(true);
		};

		const handleDisableClick = () => {
			handleMenuClose();
			if (rowData.is_active) {
				changeAccountIsActive(false);
			} else {
				setToast({ severity: 'info', message: `User account ${rowData.id} is already disabled`, open: true });
			}
		};
		
		const handleEnableClick = () => {
			handleMenuClose();
			if (!rowData.is_active) {
				changeAccountIsActive(true);
			} else {
				setToast({ severity: 'info', message: `User account ${rowData.id} is already enabled`, open: true });
			}
		};

		const handleDeleteClick = () => {
			handleMenuClose();
			setOpenDeleteDialog(true);
		};

		// Delete account dialog
		const handleDeleteDialogClose = () => {
			setOpenDeleteDialog(false);
		};

		const handleConfirmDelete = () => {
			handleDeleteDialogClose();
			deleteUserAccount();
		};

		function DeleteDialog() {
			return (
				<Dialog
					open={openDeleteDialog}
					onClose={handleDeleteDialogClose}
				>
					<DialogTitle>{"Are you sure you want to delete this account?"}</DialogTitle>
					<DialogContent>
						<DialogContentText sx={{ textAlign: 'center' }}>
							This action cannot be undone!<br /><br />To temporarily revoke access for this account, click the "Disable Account" option instead.
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleDeleteDialogClose}>
							Cancel
						</Button>
						<Button variant="contained" onClick={handleConfirmDelete}>
							Delete
						</Button>
					</DialogActions>
				</Dialog>
			);
		}

		return (
			<div>
				<IconButton
					aria-label="more"
					aria-controls="long-menu"
					aria-haspopup="true"
					onClick={handleMenuClick}
				>
					<MoreVertIcon />
				</IconButton>
				<Menu
					id="long-menu"
					anchorEl={anchorEl}
					keepMounted
					open={Boolean(anchorEl)}
					onClose={handleMenuClose}
				>
					{ !rowData.is_superuser && (
						<Tooltip title="Edit this user's locations" placement="left">
							<MenuItem onClick={handleEditClick}>
								<ListItemIcon>
									<EditLocationIcon fontSize="small" />
								</ListItemIcon>
								Edit Locations
							</MenuItem>
						</Tooltip>
					)}
					<Tooltip title="Send a password reset email" placement="left">
						<MenuItem onClick={handlePasswordResetClick}>
							<ListItemIcon>
								<LockResetIcon fontSize="small" />
							</ListItemIcon>
							Reset Password
						</MenuItem>
					</Tooltip>
					{ !rowData.is_self && (
						<Tooltip
							title={
								rowData.password_reset
									? "The user will be required to reset their password on next login"
									: "Require password reset on next login"
							}
							placement="left"
						>
							<MenuItem
								onClick={handleForcedPassResetClick}
								disabled={rowData.password_reset}
								style={rowData.password_reset ? { pointerEvents: 'auto' } : {}}
							>
								<ListItemIcon>
									<PasswordIcon fontSize="small" />
								</ListItemIcon>
								Password Reset Next Login{rowData.password_reset ? " (Set)" : ""}
							</MenuItem>
						</Tooltip>
					)}
					<Tooltip title="Duplicate this user account" placement="left">
						<MenuItem onClick={handleCloneUserClick}>
							<ListItemIcon>
								<ContentCopyIcon fontSize="small" />
							</ListItemIcon>
							Clone User
						</MenuItem>
					</Tooltip>
					{ !rowData.is_self && (
						rowData.is_active ? (
							<Tooltip title="Disable this user account" placement="left">
								<MenuItem onClick={handleDisableClick}>
									<ListItemIcon>
										<BlockIcon fontSize="small" />
									</ListItemIcon>
									Disable User
								</MenuItem>
							</Tooltip>
						) : (
							<Tooltip title="Enable this user account" placement="left">
								<MenuItem onClick={handleEnableClick}>
									<ListItemIcon>
										<BlockIcon fontSize="small" />
									</ListItemIcon>
									Enable User
								</MenuItem>
							</Tooltip>
						)
					)}
					<Tooltip title="Delete this user account" placement="left">
						<MenuItem onClick={handleDeleteClick} sx={{ color: '#F22635' }}>
							<ListItemIcon>
								<DeleteIcon fontSize="small" sx={{color: '#F22635'}}/>
							</ListItemIcon>
							Delete User
						</MenuItem>
					</Tooltip>
					<DeleteDialog />
					<PasswordResetDialog
						emailAddress={rowData.email}
						dialogOpen={openPasswordResetDialog}
						setDialogOpen={setOpenPasswordResetDialog}
					/>
				</Menu>
			</div>
		);
	}

	return (
		<Paper
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: '5px',
				}}
			>
				<Button
					onClick={handleNewUserClick}
					variant='contained'
					size='small'
				>
					<CreateIcon sx={{ mr: 1 }} />
					New User
				</Button>
				<Typography
					variant='h5'
					fontFamily='Saira, sans-serif'
					fontWeight='600'
					color='#0F1820'
					align='center'
				>
					User Management
				</Typography>
				<div /> {/* Placeholder to balance the flexbox */}
			</Box>
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
			/>
			<TempPassDialog
				tempPassOpen={tempPassOpen.open}
				setTempPassOpen={() => setTempPassOpen({ open: false, user: '', password: '' })}
				username={tempPassOpen.user}
				tempPassword={tempPassOpen.password}
			/>
			<CreateUserDialog
				creatorUser={user}
				dialogOpen={createDialogOpen}
				setDialogOpen={handleNewUserDialogOpen}
				cloneUser={cloneUser}
			/>
			<DataGrid
				getRowId={(row) => row.id}
				rows={userList}
				columns={userColumns}
				disableRowSelectionOnClick
				autoPageSize
				density={density}
				onDensityChange={setDensity}
				slots={{
					toolbar: (() => (
						<GridToolbarContainer>
							<GridToolbarColumnsButton />
							<GridToolbarFilterButton />
							<GridToolbarDensitySelector />
						</GridToolbarContainer>
					)),
					noRowsOverlay: NoItemsOverlay,
				}}
				slotProps={{
					noRowsOverlay: { noItemsText: 'No Users' },
				}}
				sx={{
					padding: 2,
				}}
			/>
		</Paper>
	);
}